// @flow
import { handleActions } from 'redux-actions';

import { getDatasetsSuccess } from 'data/datasets/actions';
import { getProfileSuccess } from 'data/profile/actions';
import { getLogger } from 'utils/logger';
import * as entitlementsActions from 'data/entitlements/entitlementsActions';
import * as subscriptionsActions from 'data/subscriptions/subscriptionsActions';
import type { Profile } from 'data/profile/types';
import { sanitize } from 'data/actions';
import * as localPreferences from 'utils/localPreferences';
import { Config, HOSTNAME } from 'utils/webappConfig';
import { replace } from 'connected-react-router';
import tracker from 'utils/tracker';

import * as actions from './actions';
import { AuthSession, REDUCER_KEY as AUTH_SESSION_REDUCER_KEY } from './types';

const log = getLogger('utils/auth/reducer');

// TODO: When Retrieve Changed Datasets, If Selected Dataset has Changed, Update Values

const initialState = AuthSession();
const reducer = handleActions({

  [sanitize]: (state) => state.merge({
    accessToken: !state.accessToken || 'X',
    refreshToken: !state.refreshToken || 'X',
  }),

  [actions.authLogout]: (state, {payload, asyncDispatch}) => {
    let newState = state;

    localPreferences.removeLastSession();

    switch (payload && payload.reason) {
      case 'AUTH_LOGOUT_EXPIRED':
        asyncDispatch(replace('/login?expired=1'));
        newState = initialState;
        break;
      case 'AUTH_LOGOUT_INACTIVE':
        asyncDispatch(replace('/login?inactive=1'));
        newState = initialState;
        break;
      case 'AUTH_LOGOUT_ROUTE':
      case 'AUTH_LOGOUT_USER_INITIATED':
      case 'AUTH_LOGOUT_UNAUTHORIZED':
      default:
        window.location.href = `${Config.hosts[HOSTNAME].homepage_url}`;
        break;
    }

    return newState;
  },

  [actions.getOAuthTokenSuccess]: (state, { payload: authTokenData, asyncDispatch }) => {
    asyncDispatch(entitlementsActions.getEntitlements());
    asyncDispatch(subscriptionsActions.getSubscriptions());
    return AuthSession({
      accessToken: authTokenData.accessToken,
      accessTokenExpired: authTokenData.accessTokenExpired,
      refreshToken: authTokenData.refreshToken,
      refreshTokenExpired: authTokenData.refreshTokenExpired,
      scopes: authTokenData.scopes,
      surveyRequested: authTokenData.surveyRequested,
    });
  },

  [actions.getOAuthTokenFailure]: (state, { payload: error }) => state.merge({ error }),

  [getProfileSuccess]: (state, { payload, asyncDispatch }) => {
    const profile: Profile = payload.resources && payload.resources.length && payload.resources[0];
    if (state.qcsId && profile && profile.userId !== state.qcsId) {
      log.error('QCS ID changed in existing auth session;');
      asyncDispatch(actions.authLogout());
    }
    let authSession = state;
    if (!state.qcsId && profile) {
      authSession = authSession.merge({ qcsId: profile.userId });
      asyncDispatch(actions.authLogin({ authSession, reload: false }));
    }
    return authSession;
  },

  [actions.authSelectDataset]: (state, { payload, asyncDispatch }) => {
    const { dataset, reload = true, location } = payload;
    log.info('Auth: Selecting dataset', dataset, reload);

    const authSession = state.merge({
      datasetId: dataset.id,
      datasetPlatform: dataset.platform,
      datasetSubscriptionTier: dataset.subscriptionTier,
      datasetCreatedByClientId: dataset.createdByClientId,
    });

    if (localPreferences.getKeepLoggedIn()) {
      localPreferences.setLastSession({
        userId: authSession.qcsId,
        datasetId: authSession.datasetId,
      });
    } else {
      localPreferences.removeLastSession();
    }

    if (state !== authSession) {
      asyncDispatch(actions.authLogin({ authSession, reload, location }));
    }

    tracker.resetActiveTimestamp();

    return reload ? state : authSession;
  },

  [getDatasetsSuccess]: (state, { payload: data, asyncDispatch }) => {
    if (state.datasetId) {
      const dataset = data.resources.find((resource) => state.datasetId === resource.id);
      if (dataset) {
        const authSession = state.merge({
          datasetPlatform: dataset.platform,
          datasetSubscriptionTier: dataset.subscriptionTier,
          datasetCreatedByClientId: dataset.createdByClientId,
        });
        if (state !== authSession) {
          asyncDispatch(actions.authLogin({ authSession, reload: false }));
        }
        return authSession;
      }
    }
    return state;
  },

  [actions.refreshedOAuthToken]: (state, { payload: authTokenData, asyncDispatch }) => {
    const authSession = state.merge(authTokenData);
    if (state !== authSession) {
      asyncDispatch(actions.persistAuthSession(authSession));
    }
    return authSession;
  },

  // This action is used to directly set contents of the AuthSession both in Redux and Persistently. This action will
  // not trigger any type of AuthLogin action and/or redirection.
  //
  // This can be used for setting non "authenticated" data stored in the auth-session or for debugging purposes. For
  // updating any authenticated data, use the other AUTH actions.
  //
  [actions.setAuthSession]: (state, { payload: authSession, asyncDispatch }) => {
    if (state !== authSession) {
      asyncDispatch(actions.persistAuthSession(authSession));
    }
    return authSession;
  },
}, initialState);

export const REDUCER_KEY = AUTH_SESSION_REDUCER_KEY;
export default reducer;
